import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { logout as apiLogout } from "@/api/auth-api";
import { useAuthStore } from "@/store/auth-store";

/**
 * Wraps the auth/logout round-trip so callers can fire-and-forget. We
 * always clear the in-memory session and route to /login regardless of
 * the API response, since the user's intent is unambiguous and a stuck
 * "logging out…" state would be worse than a stale refresh cookie that
 * the next login will rotate anyway.
 */
export function useLogout(): () => Promise<void> {
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();

  return useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // Network errors or 401s are non-actionable here — the local
      // session is already gone after clearAuth, and the refresh
      // cookie will be invalidated by the next successful login.
    }
    clearAuth();
    navigate("/login", { replace: true });
  }, [clearAuth, navigate]);
}
